import React, { MouseEvent } from 'react';
import { Switch } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import Loading from './Loading';

interface Props {
    value: boolean;
    onChange: (enabled: boolean) => void;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    text?: string;
    width?: number;
    height?: number;
    disabled?: boolean;
    ariaDisabled?: boolean;
    loading?: boolean;
    activeColor?: string;
}

const getTrackColor = (
    value: boolean,
    disabled: boolean,
    loading: boolean,
    activeColor: string,
) => {
    if (loading) {
        return 'bg-transparent';
    }
    if (disabled) {
        return value ? 'bg-main' : 'bg-gray-300';
    }
    return value ? activeColor : 'bg-gray-400';
};

const getThumbTransform = (shifted: boolean, translate: number) => {
    if (!shifted) {
        return 'translateX(0px)';
    }
    return `translate(${document.dir === 'rtl' ? -translate : translate}px)`;
};

const Toggle = ({
    value,
    onChange,
    onClick,
    text = '',
    width = 40,
    height = 24,
    disabled = false,
    ariaDisabled = false,
    loading = false,
    activeColor = 'bg-main',
}: Props) => {
    const padding = 3;

    const diameter = height - 2 * padding;
    const translate = value ? width - 2 * padding - diameter : 0;

    return (
        <Switch
            data-test-id="simple-loading-switch"
            disabled={disabled}
            aria-disabled={ariaDisabled || undefined}
            checked={value}
            onChange={disabled || ariaDisabled || loading ? null : onChange}
            onClick={onClick}
            className={twMerge(
                'relative inline-flex items-center rounded-full align-bottom transition-all duration-300',
                getTrackColor(value, disabled, loading, activeColor),
                value ? 'disabled:opacity-50 aria-disabled:opacity-50' : '',
                value && !loading && !ariaDisabled ? 'hover:darken-2' : '',
            )}
            style={{ height, width: loading ? 24 : width, padding }}
        >
            <span className="sr-only">{text}</span>

            <span
                className={twMerge(
                    'inline-block transform rounded-full transition rtl:translate-x-3',
                    disabled && !value ? 'bg-gray-200' : 'bg-white',
                )}
                style={{
                    width: diameter,
                    height: diameter,
                    transform: getThumbTransform(value && !loading, translate),
                }}
            >
                {loading ? (
                    <Loading
                        size={24}
                        className="m-[-3px]"
                        disableTimeout={true}
                        overrideColor="fill-gray-400"
                    />
                ) : null}
            </span>
        </Switch>
    );
};

export default Toggle;
